import { supabaseGestionPension as supabase } from '../config/settings/supabase-client'
import { SUPPORT_STATUS } from '../config/settings/support'
import { getLogger } from '../core/debuggers'

const logger = getLogger('support.js')

export const SUPPORT_TICKETS_PAGE_SIZE = 20

const mapTicket = (row) => {
  if (!row) return null
  const profile = row.profiles || null
  return {
    id: row.id,
    userId: row.user_id,
    trackingCode: row.tracking_code,
    category: row.category,
    message: row.message,
    status: row.status,
    createdAt: row.created_at,
    profile: profile
      ? {
          fullName: profile.full_name,
          email: profile.email,
          whatsapp: profile.whatsapp,
        }
      : null,
  }
}

/**
 * Create a support / feedback ticket for the authenticated user.
 */
export const createSupportTicket = async ({ userId, category, message }) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .insert({
      user_id: userId,
      category,
      message: message.trim(),
      status: SUPPORT_STATUS.OPEN,
    })
    .select('*')
    .single()

  if (error) {
    logger.error('createSupportTicket error:', error)
    throw error
  }

  return mapTicket(data)
}

/**
 * Fetch tickets for the current user (own tickets).
 */
export const fetchMySupportTickets = async (userId) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    logger.error('fetchMySupportTickets error:', error)
    throw error
  }

  return (data || []).map(mapTicket)
}

/**
 * Admin: fetch all tickets with profile contact info.
 */
export const fetchAllSupportTickets = async ({
  status = null,
  page = 0,
  pageSize = SUPPORT_TICKETS_PAGE_SIZE,
} = {}) => {
  const from = page * pageSize
  const to = from + pageSize - 1

  let query = supabase
    .from('support_tickets')
    .select(
      `
      id,
      user_id,
      tracking_code,
      category,
      message,
      status,
      created_at,
      profiles!user_id (
        full_name,
        email,
        whatsapp
      )
    `,
      { count: 'exact' }
    )
    .order('created_at', { ascending: false })
    .range(from, to)

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error, count } = await query

  if (error) {
    logger.error('fetchAllSupportTickets error:', error)
    throw error
  }

  return {
    data: (data || []).map(mapTicket),
    total: count ?? 0,
    page,
    pageSize,
  }
}

/**
 * Admin: update ticket status (e.g. open → closed).
 */
export const updateSupportTicketStatus = async (id, status) => {
  const { data, error } = await supabase
    .from('support_tickets')
    .update({ status })
    .eq('id', id)
    .select(
      `
      id,
      user_id,
      tracking_code,
      category,
      message,
      status,
      created_at,
      profiles!user_id (
        full_name,
        email,
        whatsapp
      )
    `
    )
    .maybeSingle()

  if (error) {
    logger.error('updateSupportTicketStatus error:', error)
    throw error
  }

  if (!data) {
    throw new Error(
      'No se pudo actualizar el ticket. Verifica que exista una política RLS de UPDATE en support_tickets.'
    )
  }

  return mapTicket(data)
}
